/*
 * Lemonade Training Oy — all rights reserved.
 * useEffectiveRegistry — React hook around the `getEffectiveRegistry` callable.
 *
 * Loads the effective MovementRegistry for the active box (or global when no
 * box context). Box-scoped entries shadow global entries server-side via the
 * existing Cloud Function (LT-298). Tombstoned (soft-deleted) entries are
 * filtered out so consumers don't have to.
 */

import { useEffect, useMemo, useRef, useState } from "react";

import { getEffectiveRegistry, type MovementRegistryEntryWithMeta } from "../api/movementRegistry";

export type RegistryStatus = "idle" | "loading" | "ready" | "error";

export interface UseEffectiveRegistryResult {
  status: RegistryStatus;
  movements: MovementRegistryEntryWithMeta[];
  error: Error | null;
}

/**
 * Module-level cache and inflight map keyed by boxID. The effective registry
 * rarely changes within a session; refetching once per movement picker mount
 * would be wasteful. `null` boxID means "global only" (no box scope).
 */
const cache = new Map<string, MovementRegistryEntryWithMeta[]>();
const inflight = new Map<string, Promise<MovementRegistryEntryWithMeta[]>>();

const cacheKey = (boxID: string | null | undefined): string => boxID ?? "__global__";

/** Test-only: clear the module-level cache so tests don't leak state. */
export function __resetEffectiveRegistryCache(): void {
  cache.clear();
  inflight.clear();
}

export function useEffectiveRegistry(
  boxID: string | null | undefined,
  { enabled = true }: { enabled?: boolean } = {}
): UseEffectiveRegistryResult {
  const [status, setStatus] = useState<RegistryStatus>("idle");
  const [movements, setMovements] = useState<MovementRegistryEntryWithMeta[]>([]);
  const [error, setError] = useState<Error | null>(null);
  const cancelledRef = useRef(false);

  useEffect(() => {
    cancelledRef.current = false;
    if (!enabled) return undefined;
    const key = cacheKey(boxID);

    const cached = cache.get(key);
    if (cached) {
      setStatus("ready");
      setMovements(cached);
      setError(null);
      return;
    }

    setStatus("loading");
    setError(null);

    let promise = inflight.get(key);
    if (!promise) {
      promise = getEffectiveRegistry({ boxID: boxID ?? null }).then((res) => {
        const live = (res.movements ?? []).filter((m) => !m.deletedAt);
        cache.set(key, live);
        return live;
      });
      inflight.set(key, promise);
      // Cleanup must absorb the rejection so it isn't reported as unhandled —
      // per-mount consumers below attach their own `.catch` for surfacing.
      const clear = (): void => {
        inflight.delete(key);
      };
      promise.then(clear, clear);
    }

    promise
      .then((live) => {
        if (cancelledRef.current) return;
        setMovements(live);
        setStatus("ready");
      })
      .catch((err: unknown) => {
        if (cancelledRef.current) return;
        setError(err instanceof Error ? err : new Error(String(err)));
        setStatus("error");
      });

    return () => {
      cancelledRef.current = true;
    };
  }, [boxID, enabled]);

  return useMemo(() => ({ status, movements, error }), [status, movements, error]);
}
